import { useState } from 'react';

import type { CompanyGroupDetailResponse, CompanyResponse } from '../../api';
import {
  Form,
  FormError,
  Panel,
  SubmitButton,
  TextField,
  fieldErrors,
  useNotifications,
} from '../../components';
import { useSetCompanyFileNameLabel, useSetGroupFileNameLabel } from '../queries';
import { ONBOARDING_COPY, fileNameLabelSource, type FileNameLabelSource } from './onboarding';

/**
 * The name insurer files carry in place of the registered name: on a company,
 * and on an economic group for the members without one of their own.
 *
 * The same form for both. It starts on the label the server last reported, a
 * save that restates it is answered on the screen rather than with a request
 * that would change nothing, and clearing is its own button -- the API clears
 * with `null`, and an emptied text box is refused rather than read as "remove".
 * A `422` lands on the field, worded for whoever typed it.
 */
function FileNameLabelForm({
  title,
  current,
  pending,
  error,
  onSave,
  onClear,
}: {
  title: string;
  current: string | null;
  pending: boolean;
  error: unknown;
  onSave: (label: string, done: () => void) => void;
  onClear: (done: () => void) => void;
}) {
  const [draft, setDraft] = useState(current ?? '');
  const [localError, setLocalError] = useState<string | null>(null);
  const rejected = fieldErrors(error)['file_name_label'] !== undefined;
  const inlineError = localError ?? (rejected ? ONBOARDING_COPY.fileNameLabelRejected : undefined);

  const save = () => {
    const label = draft.trim();
    if (label === '') {
      setLocalError(ONBOARDING_COPY.fileNameLabelBlank);
      return;
    }
    if (label === current) {
      setLocalError(ONBOARDING_COPY.fileNameLabelUnchanged);
      return;
    }
    setLocalError(null);
    onSave(label, () => setDraft(label));
  };

  const clear = () => {
    setLocalError(null);
    onClear(() => setDraft(''));
  };

  return (
    <Form onSubmit={save} aria-label={title}>
      {/* The field carries a refusal it can name; anything else is above it. */}
      {!rejected && <FormError error={error} />}
      <TextField
        label={ONBOARDING_COPY.fileNameLabelLabel}
        value={draft}
        onChange={(value) => {
          setDraft(value);
          setLocalError(null);
        }}
        hint={ONBOARDING_COPY.fileNameLabelHint}
        autoComplete="off"
        {...(inlineError !== undefined && { error: inlineError })}
      />
      <div className="form__actions">
        <SubmitButton pending={pending} pendingLabel={ONBOARDING_COPY.fileNameLabelSaving}>
          {ONBOARDING_COPY.fileNameLabelSave}
        </SubmitButton>
        {current !== null && (
          <button
            type="button"
            className="button button--secondary"
            disabled={pending}
            onClick={clear}
          >
            {ONBOARDING_COPY.fileNameLabelClear}
          </button>
        )}
      </div>
    </Form>
  );
}

/**
 * The name in force is shown as the setting it is: a text box, read-only, above
 * the one that changes it.
 */
const noop = () => {};

const IN_FORCE: Record<FileNameLabelSource, string> = {
  own: ONBOARDING_COPY.fileNameLabelInForceOwn,
  group: ONBOARDING_COPY.fileNameLabelInForceGroup,
  registered: ONBOARDING_COPY.fileNameLabelInForceRegistered,
};

/** The company's own label, and which name its files use right now. */
export function CompanyFileNameLabelPanel({ company }: { company: CompanyResponse }) {
  const setLabel = useSetCompanyFileNameLabel();
  const { notify } = useNotifications();

  const current = company.file_name_label ?? null;
  const source = fileNameLabelSource(company);
  const inForce = company.file_name_label_in_force ?? company.name;

  return (
    <Panel
      title={ONBOARDING_COPY.fileNameLabelTitle}
      description={ONBOARDING_COPY.fileNameLabelDescription}
    >
      <div data-testid="file-name-label-in-force">
        <TextField label={IN_FORCE[source]} value={inForce} onChange={noop} disabled />
      </div>
      <FileNameLabelForm
        title={ONBOARDING_COPY.fileNameLabelTitle}
        current={current}
        pending={setLabel.isPending}
        error={setLabel.error}
        onSave={(label, done) =>
          setLabel.mutate(
            { companyId: company.id, label },
            {
              onSuccess: () => {
                done();
                notify('success', ONBOARDING_COPY.fileNameLabelSaved);
              },
            },
          )
        }
        onClear={(done) =>
          setLabel.mutate(
            { companyId: company.id, label: null },
            {
              onSuccess: () => {
                done();
                notify('success', ONBOARDING_COPY.fileNameLabelCleared);
              },
            },
          )
        }
      />
    </Panel>
  );
}

/** The group's label, which members without their own use. */
export function GroupFileNameLabelPanel({ group }: { group: CompanyGroupDetailResponse }) {
  const setLabel = useSetGroupFileNameLabel();
  const { notify } = useNotifications();
  const current = group.file_name_label ?? null;

  return (
    <Panel
      title={ONBOARDING_COPY.fileNameLabelTitle}
      description={ONBOARDING_COPY.fileNameLabelGroupDescription}
    >
      <TextField
        label={ONBOARDING_COPY.fileNameLabelInForceGroup}
        value={current ?? ''}
        onChange={noop}
        disabled
        {...(current === null && { hint: ONBOARDING_COPY.fileNameLabelNone })}
      />
      <FileNameLabelForm
        title={ONBOARDING_COPY.fileNameLabelTitle}
        current={current}
        pending={setLabel.isPending}
        error={setLabel.error}
        onSave={(label, done) =>
          setLabel.mutate(
            { groupId: group.id, label },
            {
              onSuccess: () => {
                done();
                notify('success', ONBOARDING_COPY.fileNameLabelSaved);
              },
            },
          )
        }
        onClear={(done) =>
          setLabel.mutate(
            { groupId: group.id, label: null },
            {
              onSuccess: () => {
                done();
                notify('success', ONBOARDING_COPY.fileNameLabelCleared);
              },
            },
          )
        }
      />
    </Panel>
  );
}
